import classNames from "classnames";
import ToolType from "../../types/ToolType";
import ImpactButton from "../ImpactButton";
import BottomModal from "./BottomModal";
import ToolTypeIcon from "../ToolTypeIcon";
import BrushSample from "./BrushSample";
import { useAtom, useAtomValue } from "jotai";
import {
  brushSizeAtom,
  brushTypeAtom,
  colourChoiceAtom,
  toolsOpenAtom,
} from "./editorAtoms";
import BottomModalHorizontalScroll from "./BottomModalHorizontalScroll";

const ToolPicker = () => {
  const [toolsOpen, setToolsOpen] = useAtom(toolsOpenAtom);
  const [brushType, setBrushType] = useAtom(brushTypeAtom);
  const brushSizes = [
    {
      name: "Small",
      value: 20,
    },
    {
      name: "Medium",
      value: 60,
    },
    {
      name: "Large",
      value: 80,
    },
    {
      name: "Extra Large",
      value: 100,
    },
  ];
  const [currentBrushSize, setCurrentBrushSize] = useAtom(brushSizeAtom);
  const colourChoice = useAtomValue(colourChoiceAtom);

  const handleToolClick = (tool: ToolType) => {
    setBrushType(tool);
    setToolsOpen(false);
  };

  const handleClose = () => {
    setToolsOpen(false);
  };

  return (
    <BottomModal open={toolsOpen} onClose={handleClose}>
      <BottomModalHorizontalScroll name="tools" className="px-5 pt-5 pb-5">
        {[
          ToolType.Brush,
          ToolType.Eraser,
          ToolType.Bucket,
          ToolType.Spray,
          ToolType.Splatter,
          ToolType.Zig,
        ].map((tool) => (
          <ImpactButton
            key={tool}
            onClick={() => handleToolClick(tool)}
            className={classNames(
              "relative rounded-lg border shadow dark:border-gray-800",
              { "bg-gray-100 dark:bg-gray-600": tool === brushType }
            )}
          >
            <div
              className={classNames(
                "inline absolute p-1 -top-2 -left-2 text-gray-600 border shadow rounded-full dark:text-gray-50 dark:border-gray-800",
                {
                  "bg-gray-100 dark:bg-gray-600": tool === brushType,
                  "bg-gray-50 dark:bg-gray-700": tool !== brushType,
                }
              )}
            >
              <ToolTypeIcon toolType={tool} />
            </div>
            <div className="flex items-center border-b dark:border-gray-800 justify-center px-2 text-sm font-medium text-gray-500 dark:text-gray-50 whitespace-nowrap text-nowrap">
              {tool}
            </div>
            <div>
              <BrushSample
                type={tool}
                sampleSize={100}
                brushSize={20}
                padding={10}
              />
            </div>
          </ImpactButton>
        ))}
      </BottomModalHorizontalScroll>

      <BottomModalHorizontalScroll name="brushSize" className="pb-5 px-5">
        {brushSizes.map((size) => (
          <ImpactButton
            key={size.value}
            onClick={() => {
              setCurrentBrushSize(size.value);
            }}
            className={classNames(
              "relative rounded-lg border shadow dark:border-gray-800",
              {
                "bg-gray-100 dark:bg-gray-600": size.value === currentBrushSize,
              }
            )}
          >
            <div className="flex items-center border-b dark:border-gray-800 justify-center px-2 text-sm font-medium text-gray-500 dark:text-gray-50 whitespace-nowrap text-nowrap">
              {size.name}
            </div>
            <div className="w-[100px] h-[100px] p-2 flex items-center justify-center">
              <svg color={colourChoice.colourModifier} viewBox={"0 0 110 110"}>
                <title>{size.name} Brush Size</title>
                <ellipse
                  cx="55"
                  cy="55"
                  rx={size.value / 2}
                  ry={size.value / 2}
                  fill="currentColor"
                />
              </svg>
            </div>
          </ImpactButton>
        ))}
      </BottomModalHorizontalScroll>
    </BottomModal>
  );
};
export default ToolPicker;
